if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.handleThumbnailResize = debounce(this.syncThumbnailSliderHeight.bind(this), 150);
        this.handleThumbnailScroll = debounce(this.updateThumbnailSliderControls.bind(this), 50);
        this.thumbnailObservedImages = Array.from(this.elements.viewer.querySelectorAll('img'));

        this.elements.thumbnails.slider?.addEventListener('scroll', this.handleThumbnailScroll);
        this.elements.thumbnails.querySelectorAll('.slider-button').forEach((button) => {
          button.addEventListener('click', this.onThumbnailSliderButtonClick.bind(this));
        });
        this.thumbnailObservedImages.forEach((image) => image.addEventListener('load', this.handleThumbnailResize));
        window.addEventListener('resize', this.handleThumbnailResize);

        if (window.ResizeObserver) {
          this.thumbnailResizeObserver = new ResizeObserver(this.handleThumbnailResize);
          this.thumbnailResizeObserver.observe(this.elements.viewer);
        }

        this.syncThumbnailSliderHeight();
        window.requestAnimationFrame(() => this.syncThumbnailSliderHeight());
        window.setTimeout(() => this.syncThumbnailSliderHeight(), 500);
      }

      disconnectedCallback() {
        this.elements.thumbnails?.slider?.removeEventListener('scroll', this.handleThumbnailScroll);
        this.thumbnailObservedImages?.forEach((image) => image.removeEventListener('load', this.handleThumbnailResize));
        if (this.handleThumbnailResize) window.removeEventListener('resize', this.handleThumbnailResize);
        this.thumbnailResizeObserver?.disconnect();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateGalleryPagination(thumbnail);
        this.updateThumbnailSliderControls();

        if (this.isVerticalThumbnailLayout()) {
          const slider = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = slider.scrollTop;
          const visibleBottom = visibleTop + slider.clientHeight;

          if (thumbnailTop >= visibleTop && thumbnailBottom <= visibleBottom) return;

          slider.scrollTo({ top: thumbnailTop });
          window.setTimeout(() => this.updateThumbnailSliderControls(), 250);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      updateGalleryPagination(thumbnail) {
        if (!thumbnail) return;

        const current = this.elements.viewer?.querySelector('.slider-counter--current');
        const total = this.elements.viewer?.querySelector('.slider-counter--total');
        const paginationItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        const currentPosition = paginationItems.indexOf(thumbnail) + 1;

        if (current && currentPosition > 0) current.textContent = currentPosition;
        if (total && paginationItems.length) total.textContent = paginationItems.length;
      }

      syncThumbnailSliderHeight() {
        if (!this.classList.contains('single-product__media-gallery') || !this.elements.thumbnails || !this.elements.viewer) {
          return;
        }

        if (window.matchMedia('(max-width: 757.98px)').matches) {
          this.elements.thumbnails.style.removeProperty('--single-product-gallery-viewer-height');
          this.elements.thumbnails.classList.remove('is-vertical-scrollable');
          return;
        }

        const viewerHeight = this.elements.viewer.getBoundingClientRect().height;
        if (viewerHeight > 0) {
          this.elements.thumbnails.style.setProperty('--single-product-gallery-viewer-height', `${viewerHeight}px`);
        }

        this.updateThumbnailSliderControls();
      }

      updateThumbnailSliderControls() {
        if (!this.elements.thumbnails?.slider || !this.isVerticalThumbnailLayout()) return;

        const thumbnailSlider = this.elements.thumbnails;
        const slider = thumbnailSlider.slider;
        const maxScroll = slider.scrollHeight - slider.clientHeight;
        const isScrollable = maxScroll > 1;
        const prevButton = thumbnailSlider.querySelector('.slider-button--prev');
        const nextButton = thumbnailSlider.querySelector('.slider-button--next');

        thumbnailSlider.classList.toggle('is-vertical-scrollable', isScrollable);

        if (!prevButton || !nextButton) return;

        if (!isScrollable) {
          prevButton.setAttribute('disabled', 'disabled');
          nextButton.setAttribute('disabled', 'disabled');
          return;
        }

        prevButton.toggleAttribute('disabled', slider.scrollTop <= 1);
        nextButton.toggleAttribute('disabled', slider.scrollTop >= maxScroll - 1);
      }

      onThumbnailSliderButtonClick(event) {
        if (!this.elements.thumbnails?.slider || !this.isVerticalThumbnailLayout()) return;

        event.preventDefault();

        const slider = this.elements.thumbnails.slider;
        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const visibleThumbnail = Array.from(slider.querySelectorAll('[data-target]')).find(
          (item) => item.offsetParent !== null
        );
        const sliderStyles = window.getComputedStyle(slider);
        const itemGap = parseFloat(sliderStyles.rowGap || sliderStyles.gap) || 0;
        const scrollStep = visibleThumbnail
          ? visibleThumbnail.getBoundingClientRect().height + itemGap
          : slider.clientHeight * 0.8;

        slider.scrollTo({
          top: slider.scrollTop + direction * scrollStep,
          behavior: 'smooth',
        });

        window.setTimeout(() => this.updateThumbnailSliderControls(), 250);
      }

      isVerticalThumbnailLayout() {
        if (!this.elements.thumbnails?.slider) return false;

        return window.getComputedStyle(this.elements.thumbnails.slider).flexDirection === 'column';
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
